import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Gift,
  Clock,
  CheckCircle,
  DollarSign,
  Calendar,
  ArrowRight,
  AlertTriangle,
  PlayCircle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { ConfirmModal } from '@/components/ui/Modal';
import { useReferralStats, useValidateAllReferrals } from '@/hooks/useReferrals';
import { TIER_CONFIG, type ReferralTier } from '@/types/referral.types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  variant = 'default',
}) => {
  const variantClasses = {
    default: 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-400',
    success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    danger: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    info: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {value}
            </p>
          </div>
          <div className={`p-3 rounded-xl ${variantClasses[variant]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TierCard: React.FC<{ tier: ReferralTier; count: number }> = ({ tier, count }) => {
  const config = TIER_CONFIG[tier];

  return (
    <div className={`p-4 rounded-lg ${config.bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`font-semibold ${config.color}`}>{config.label}</span>
        <Badge variant="secondary" className="text-xs">
          {config.minReferrals}-{config.maxReferrals || '+'}
        </Badge>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">usuarios</p>
    </div>
  );
};

export default function ReferralsDashboard() {
  const { data: stats, isLoading, isError } = useReferralStats();
  const validateAllMutation = useValidateAllReferrals();
  const [showValidateAllModal, setShowValidateAllModal] = useState(false);

  const handleValidateAll = async () => {
    await validateAllMutation.mutateAsync();
    setShowValidateAllModal(false);
  };

  if (isLoading) {
    return <PageLoader message="Carregando estatisticas..." />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Erro ao carregar dados
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Nao foi possivel carregar as estatisticas. Tente novamente.
        </p>
      </div>
    );
  }

  // Map API response to expected format
  // API returns: totalReferrals, pendingReferrals, validatedReferrals, totalPremiumDaysGiven, totalCreditsGiven, tierDistribution
  const safeStats = {
    total: stats?.totalReferrals ?? 0,
    pending: stats?.pendingReferrals ?? 0,
    validated: stats?.validatedReferrals ?? 0,
    rejected: stats?.rejectedReferrals ?? 0,
    totalCreditsAwarded: stats?.totalCreditsGiven ?? 0,
    totalPremiumDaysAwarded: stats?.totalPremiumDaysGiven ?? 0,
    byTier: {
      bronze: stats?.tierDistribution?.bronze ?? 0,
      prata: stats?.tierDistribution?.silver ?? 0,
      ouro: stats?.tierDistribution?.gold ?? 0,
      embaixador: stats?.tierDistribution?.ambassador ?? 0,
    },
    topReferrers: (stats?.topReferrers ?? []).map((user) => ({
      id: user.userId ?? '',
      name: user.name ?? '',
      phoneNumber: user.phoneNumber ?? '',
      tier: (user.tier as ReferralTier) ?? 'bronze',
      totalReferrals: user.validated ?? 0,
      credits: user.credits ?? 0,
    })),
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Sistema de Indicacoes
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Visao geral do programa de referral
          </p>
        </div>
        {safeStats.pending > 0 && (
          <Button
            onClick={() => setShowValidateAllModal(true)}
            leftIcon={<PlayCircle className="w-4 h-4" />}
          >
            Validar Todas ({safeStats.pending})
          </Button>
        )}
      </div>

      {/* Main stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total de Indicacoes"
          value={safeStats.total.toLocaleString()}
          icon={<Users className="w-6 h-6" />}
          variant="default"
        />
        <StatCard
          title="Pendentes"
          value={safeStats.pending}
          icon={<Clock className="w-6 h-6" />}
          variant="warning"
        />
        <StatCard
          title="Validadas"
          value={safeStats.validated}
          icon={<CheckCircle className="w-6 h-6" />}
          variant="success"
        />
        <StatCard
          title="Creditos (R$)"
          value={`R$ ${safeStats.totalCreditsAwarded.toLocaleString()}`}
          icon={<DollarSign className="w-6 h-6" />}
          variant="info"
        />
        <StatCard
          title="Dias Premium"
          value={safeStats.totalPremiumDaysAwarded.toLocaleString()}
          icon={<Calendar className="w-6 h-6" />}
          variant="success"
        />
      </div>

      {/* Tier distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Distribuicao por Tier</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <TierCard tier="bronze" count={safeStats.byTier.bronze} />
            <TierCard tier="prata" count={safeStats.byTier.prata} />
            <TierCard tier="ouro" count={safeStats.byTier.ouro} />
            <TierCard tier="embaixador" count={safeStats.byTier.embaixador} />
          </div>
        </CardContent>
      </Card>

      {/* Top referrers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Top Referrers</CardTitle>
          <Link
            to="/admin/referrals/users"
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {safeStats.topReferrers.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              Nenhum referrer ainda
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Usuario
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Tier
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Indicacoes
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Creditos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {safeStats.topReferrers.map((user, index) => {
                    const tierConfig = TIER_CONFIG[user.tier];
                    return (
                      <tr
                        key={user.id}
                        className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                      >
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              #{index + 1}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {user.name || 'Sem nome'}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {user.phoneNumber}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${tierConfig.bgColor} ${tierConfig.color}`}
                          >
                            {tierConfig.label}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right font-medium text-gray-900 dark:text-white">
                          {user.totalReferrals ?? 0}
                        </td>
                        <td className="py-3 px-2 text-right font-medium text-gray-900 dark:text-white">
                          R$ {(user.credits ?? 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/admin/referrals/list">
          <Card className="hover:bg-gray-50 dark:hover:bg-secondary-700/50 transition-colors cursor-pointer">
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                  <Gift className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Gerenciar Indicacoes
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Validar, rejeitar e visualizar indicacoes
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/referrals/users">
          <Card className="hover:bg-gray-50 dark:hover:bg-secondary-700/50 transition-colors cursor-pointer">
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30">
                  <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Usuarios Referrers
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Visualizar tiers, creditos e ajustes
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Validate all modal */}
      <ConfirmModal
        isOpen={showValidateAllModal}
        onClose={() => setShowValidateAllModal(false)}
        onConfirm={handleValidateAll}
        title="Validar todas as indicacoes"
        message={`Deseja validar todas as ${safeStats.pending} indicacoes pendentes? Esta acao distribuira os rewards automaticamente.`}
        confirmText="Validar Todas"
        cancelText="Cancelar"
        isLoading={validateAllMutation.isPending}
      />
    </div>
  );
}
